/**
 * The three grading modes, defined once.
 *
 * Every input block declared this same array — same ids, same icons, same order, in the same
 * cycle. Ten spelled it `MODES` with resolved labels; Numerical spelled it `GRADING_MODES` at
 * module scope with a label KEY, because it has no `t` in scope there. The only other difference
 * between the ten was whitespace padding.
 *
 * The array carries `labelKey` so it can live at module scope like Numerical's did; `gradingModes(t)`
 * resolves the labels for the ten callers that want them ready to render. The strings themselves
 * are already shared (blocks.gradingModes.*) — this is the shape around them.
 */
import { positiveTriggerFor, type GradingMode as Mode } from '@agavi/blocks';
import { CircleCheck, CircleDashed, MessageCircle } from 'lucide-react';

/** Re-exported so the ten call sites that import it from here keep working; the declaration now
 * lives in `packages/blocks`, beside the code that grades. */
export type { GradingMode } from '@agavi/blocks';

/**
 * The title of whichever positive state this mode has.
 *
 * The block asks `positiveTriggerFor` which state is live and this says what to call it — the split
 * every shared-vs-presentational pair in this file follows. It replaces an if-ladder over the three
 * modes that each block spelled out twice.
 */
const POSITIVE_TITLE_KEYS = {
  correct:      'blocks.feedbackDefaults.correctTitle',
  any_answer:   'blocks.feedbackDefaults.anyAnswerTitle',
  any_response: 'blocks.feedbackDefaults.anyResponseTitle',
} as const;

export function positiveSlotTitleFor(mode: Mode, t: (key: string) => string): string {
  return t(POSITIVE_TITLE_KEYS[positiveTriggerFor(mode)]);
}
export const GRADING_MODES = [
  { id: 'graded'   as const, labelKey: 'blocks.gradingModes.graded',   Icon: CircleCheck   },
  { id: 'survey'   as const, labelKey: 'blocks.gradingModes.survey',   Icon: MessageCircle },
  { id: 'optional' as const, labelKey: 'blocks.gradingModes.optional', Icon: CircleDashed  },
];

/** The same three with labels resolved — what a component renders from. */
export function gradingModes(t: (key: string) => string) {
  return GRADING_MODES.map(m => ({ id: m.id, label: t(m.labelKey), Icon: m.Icon }));
}
